import { Component, Input } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// Interactive CMYK mixer for the CMYK post, all sliders, no boxes. Unlike the screen
// models, CMYK is subtractive: you start at white paper and each ink slider SUBTRACTS
// light. A cyan, magenta, yellow and key (black) slider lay ink down, and two swatches
// show the bare paper next to the printed result, so dragging any ink visibly darkens
// the print away from the untouched white. The starting ink levels come from the post's
// own color via [hex] (kept as floats so the exact hex reproduces). The post marks the
// mount point with <!--CMYK-START-->...<!--CMYK-END--> around a static fallback; the page
// swaps this live component in. Same design language as the hwb-mixer widget.
@Component( {
  selector: 'app-cmyk-mixer',
  templateUrl: './cmyk-mixer.component.html',
  styleUrls: [ './cmyk-mixer.component.scss' ],
} )
export class CmykMixerComponent {

  @Input() heading = 'Try It Yourself';

  // The four inks, 0..100. Defaults to the CMYK post's Miami magenta until a hex binds.
  cyan = 0;
  magenta = 62.75;
  yellow = 36.47;
  key = 0;
  private _hex = '#FF5FA2';

  @Input() set hex( value: string ) {
    const rgb = this.parseHex( value );
    if ( !rgb ) {
      return;
    }
    this._hex = '#' + String( value ).replace( /^#/, '' ).toUpperCase();
    const [ c, m, y, k ] = this.rgbToCmyk( rgb );
    this.cyan = c;
    this.magenta = m;
    this.yellow = y;
    this.key = k;
  }
  get hex(): string {
    return this._hex;
  }

  private tracked = false;

  constructor( private analyticsService: AnalyticsService ) {
  }

  // Bare white paper, before any ink.
  get paperHex(): string {
    return '#FFFFFF';
  }

  // The printed result at the current ink levels.
  get printHex(): string {
    return this.cmykToHex( this.cyan, this.magenta, this.yellow, this.key );
  }

  // The mix written the way design tools write it (four ink percentages).
  get cmykString(): string {
    return `cmyk(${ this.cLabel }% ${ this.mLabel }% ${ this.yLabel }% ${ this.kLabel }%)`;
  }

  // Total area coverage: the sum of the four inks. Presses usually cap this around 300%,
  // so beyond that the note warns the paper is carrying more ink than it can hold.
  get totalInk(): number {
    return this.cyan + this.magenta + this.yellow + this.key;
  }
  get heavy(): boolean {
    return this.totalInk > 300;
  }

  // Tints the slider thumbs with the live print so the controls read in context.
  get thumb(): string {
    return this.printHex;
  }

  // Each ink track ramps white paper toward that ink laid down alone, so the track shows
  // what pushing it further prints.
  get cyanTrack(): string {
    return 'linear-gradient( 90deg, #ffffff, #00FFFF )';
  }
  get magentaTrack(): string {
    return 'linear-gradient( 90deg, #ffffff, #FF00FF )';
  }
  get yellowTrack(): string {
    return 'linear-gradient( 90deg, #ffffff, #FFFF00 )';
  }
  get keyTrack(): string {
    return 'linear-gradient( 90deg, #ffffff, #000000 )';
  }

  // Whole-number readouts (the values themselves stay precise).
  get cLabel(): number {
    return Math.round( this.cyan );
  }
  get mLabel(): number {
    return Math.round( this.magenta );
  }
  get yLabel(): number {
    return Math.round( this.yellow );
  }
  get kLabel(): number {
    return Math.round( this.key );
  }

  onCyan( value: string ): void {
    this.cyan = this.clamp( parseInt( value, 10 ), 0, 100 );
    this.track();
  }

  onMagenta( value: string ): void {
    this.magenta = this.clamp( parseInt( value, 10 ), 0, 100 );
    this.track();
  }

  onYellow( value: string ): void {
    this.yellow = this.clamp( parseInt( value, 10 ), 0, 100 );
    this.track();
  }

  onKey( value: string ): void {
    this.key = this.clamp( parseInt( value, 10 ), 0, 100 );
    this.track();
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'cmyk-mixer' } );
  }

  private clamp( v: number, lo: number, hi: number ): number {
    if ( isNaN( v ) ) {
      return lo;
    }
    return Math.max( lo, Math.min( hi, v ) );
  }

  private parseHex( text: string ): [ number, number, number ] | null {
    const h = String( text || '' ).trim().replace( /^#/, '' );
    if ( !/^[0-9a-fA-F]{6}$/.test( h ) ) {
      return null;
    }
    return [
      parseInt( h.slice( 0, 2 ), 16 ),
      parseInt( h.slice( 2, 4 ), 16 ),
      parseInt( h.slice( 4, 6 ), 16 ),
    ];
  }

  // [r,g,b] (0..255) to [cyan, magenta, yellow, key] (each 0..100), unrounded so the post
  // color reproduces exactly. Key is the black the three inks can't reach: whatever light
  // is left once the strongest channel is spent.
  private rgbToCmyk( rgb: [ number, number, number ] ): [ number, number, number, number ] {
    const r = rgb[ 0 ] / 255, g = rgb[ 1 ] / 255, b = rgb[ 2 ] / 255;
    const k = 1 - Math.max( r, g, b );
    if ( k >= 1 ) {
      return [ 0, 0, 0, 100 ];
    }
    const c = ( 1 - r - k ) / ( 1 - k );
    const m = ( 1 - g - k ) / ( 1 - k );
    const y = ( 1 - b - k ) / ( 1 - k );
    return [ c * 100, m * 100, y * 100, k * 100 ];
  }

  // CMYK to hex: each ink subtracts its channel of light from the white paper, and the key
  // darkens all three at once. cmyk(0 0 0 0) is the bare paper; cmyk(0 0 0 100) is the ink
  // laid solid.
  private cmykToHex( c: number, m: number, y: number, k: number ): string {
    const cN = c / 100, mN = m / 100, yN = y / 100, kN = k / 100;
    const to = ( v: number ) => Math.round( v * 255 ).toString( 16 ).padStart( 2, '0' );
    const r = ( 1 - cN ) * ( 1 - kN );
    const g = ( 1 - mN ) * ( 1 - kN );
    const b = ( 1 - yN ) * ( 1 - kN );
    return ( '#' + to( r ) + to( g ) + to( b ) ).toUpperCase();
  }
}
